import React from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";

type Props = {
  open: boolean;
  patientName?: string;
  saving?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export default function GenerateTokenConfirmModal({
  open,
  patientName,
  saving = false,
  onCancel,
  onConfirm,
}: Props) {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generate-token-title"
      onClick={saving ? undefined : onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div>
            <h2
              id="generate-token-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              Generate next token?
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {patientName
                ? `This will generate the next token number for ${patientName}.`
                : "This will generate the next token number for this appointment."}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onConfirm}
            className="px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            {saving ? "Generating…" : "Generate token"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
